import type { CardProps } from '@leafwell/components';
import type { BlockMethod, SingleMethod } from 'types/block-method';
import { Card, Paragraph, Wrap } from '@leafwell/components';
import Figure from './figure';
import HeadingBody from './heading-body';

const MethodItem: React.FC<SingleMethod & { index: number }> = ({
  image,
  index,
  items,
  title,
}) => {
  const hasImages = items.some(({ image }) => image);
  const defaultClassNames = 'grid-cols-inline-form gap-x-10';
  let cardProps: CardProps;

  if (index % 3 === 0) {
    cardProps = {
      className: [
        defaultClassNames,
        'text-white bg-lilac-600 border-lilac-600',
      ].join(' '),
      variant: 'light',
    };
  } else if (index % 3 === 1) {
    cardProps = { className: defaultClassNames, variant: 'light' };
  } else {
    cardProps = { className: defaultClassNames, variant: 'secondary' };
  }

  return (
    <Card {...cardProps} filled={true} size="large">
      <HeadingBody level="4" title={title} />
      {image && (
        <Figure
          {...image}
          className="w-10 h-10"
          classNameImage="lg:object-right"
          objectFit="contain"
        />
      )}
      {items?.length > 0 && (
        <ul
          className={[
            'col-span-full gap-4',
            hasImages
              ? 'grid md:grid-cols-3 md:gap-x-10 xl:gap-x-16'
              : 'sm:columns-2 lg:columns-4',
          ].join(' ')}
        >
          {items.map(({ content, image, label }, i) => (
            <li
              key={`${index}-${i}`}
              className={
                hasImages
                  ? 'grid grid-cols-2-auto gap-x-4 xl:gap-x-6 items-center'
                  : 'list-inside list-disc'
              }
            >
              {image && (
                <Figure
                  {...image}
                  className="row-span-full max-w-12"
                  classNameImage="w-12 h-16 md:w-full md:h-full"
                  objectFit="contain"
                />
              )}
              <div className="inline-block">
                {label && (
                  <Paragraph
                    className="font-medium"
                    dangerouslySetInnerHTML={{ __html: label }}
                    size="large"
                  />
                )}
                <Paragraph
                  className="col-start-2"
                  dangerouslySetInnerHTML={{ __html: content }}
                  size="small"
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

const Method: React.FC<BlockMethod> = ({ method, title }) => (
  <Wrap size="large" className="!gap-y-6">
    <HeadingBody
      className="col-span-full text-center max-w-xl justify-self-center"
      level="2"
      title={title}
      tagName="h3"
    />
    {method?.length > 0 ? (
      <section className="rounded-2xl overflow-hidden">
        {method.map((item, index) => (
          <MethodItem key={index} {...{ ...item, index: index + 1 }} />
        ))}
      </section>
    ) : null}
  </Wrap>
);

export default Method;
